import { ChangeEvent, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";

import { Box } from "styles/globalComponents";

import { Button } from "Components/Button";
import Input from "Components/Form/Input";
import Select from "Components/Form/Select";
import { Loader } from "Components/Loader";
import { Modal } from "Components/Modals/Modal";
import { SUL_AMERICA_STATUS } from "Constants/Orders";
import { ChangeStatusOrder, GetDetails } from "Requests/Orders";
import { createToast } from "Utils/toastFunc";
import * as OrderRequests from "Requests/Orders";
import { SelectAppointment } from "Components/SelectSchedule";
import { formatRescheduleDate } from "../utils";
import { ModalChangeStatusStyle } from "../styles";

export type StatusOption = {
  id: number | string;
  name: string;
};

interface ChangeStatusModalProps {
  isOpen: boolean;
  closeModal: () => void;
  orderId: number | null;
  statusOptions: StatusOption[];
}

export const ChangeStatusModal = ({
  isOpen,
  closeModal,
  orderId,
  statusOptions,
}: ChangeStatusModalProps) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const isRescheduled = Number(status) === SUL_AMERICA_STATUS.rescheduled;
  const shouldFetchScheduleData = isRescheduled && !!orderId;

  const homeCollectQuery = useQuery({
    queryKey: ["order-home-collect", orderId],
    queryFn: () => OrderRequests.GetDetails(orderId as number),
    enabled: shouldFetchScheduleData,
    refetchOnWindowFocus: false,
  });

  const orderDetailsQuery = useQuery({
    queryKey: ["order-details", orderId],
    queryFn: () => GetDetails(orderId as number),
    enabled: shouldFetchScheduleData,
    refetchOnWindowFocus: false,
  });

  const scheduleData = useMemo(() => {
    const homeCollect = homeCollectQuery.data?.data;
    const orderDetails = orderDetailsQuery.data?.data;
    const preOrderId = orderDetails?.pre_budget_id;

    if (!homeCollect) {
      return { cep: "", appointmentTypeID: null as number | null };
    }

    const cep =
      homeCollect.attendance.home.collect_address?.zip_code?.replace(
        /\D/g,
        ""
      ) ?? "";
    const { scheduleId } = homeCollect;

    const cartItem = orderDetails?.cart?.find(
      (item: { id_home_collected: number; appointment_type_id: number }) =>
        item.id_home_collected === scheduleId
    );

    return {
      cep,
      appointmentTypeID: cartItem?.appointment_type_id ?? null,
      preOrderId,
    };
  }, [homeCollectQuery.data, orderDetailsQuery.data]);

  const isLoadingScheduleData =
    shouldFetchScheduleData &&
    (homeCollectQuery.isLoading || orderDetailsQuery.isLoading);

  const hasScheduleDataError =
    shouldFetchScheduleData && !isLoadingScheduleData && !scheduleData.cep;

  const isSaveDisabled =
    !status ||
    !reason.trim() ||
    isSaving ||
    !orderId ||
    (isRescheduled && !rescheduleDate);

  const resetForm = () => {
    setStatus("");
    setReason("");
    setRescheduleDate("");
  };

  const handleClose = () => {
    if (isSaving) return;

    resetForm();
    closeModal();
  };

  const handleStatusChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setStatus(event.target.value);
    setRescheduleDate("");
  };

  const handleReasonChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setReason(event.target.value);
  };

  const handleSave = async () => {
    if (isSaveDisabled || !orderId) return;

    try {
      setIsSaving(true);
      await ChangeStatusOrder(
        orderId,
        Number(status),
        reason.trim(),
        isRescheduled ? formatRescheduleDate(rescheduleDate) : undefined
      );
      createToast("success", "Status do pedido alterado com sucesso");
      await queryClient.invalidateQueries(["client-orders"]);
      resetForm();
      closeModal();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Ocorreu um erro ao alterar o status do pedido";
      createToast("error", message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      closeOverlay
      isOpen={isOpen}
      maxWidth={isRescheduled ? "1000px" : "600px"}
      maxHeight={isRescheduled ? "90vh" : undefined}
      requestClose={handleClose}
      renderHeading={() => <h2>Alterar status do pedido #{orderId}</h2>}
      renderActions={() => (
        <ModalChangeStatusStyle display="flex" direction="column" gap="24">
          <Select
            id="change-status"
            label="Status*"
            placeholder="Selecione o status"
            value={status}
            onChange={handleStatusChange}
            options={statusOptions}
            optionName="name"
            required
          />

          <Input
            id="change-status-reason"
            label="Motivo da alteração*"
            type="text"
            placeholder="Informe o motivo da alteração"
            value={reason}
            onChange={handleReasonChange}
            multiline
            small
            required
            error=""
            purple={false}
            onFocus={() => undefined}
            onBlur={() => undefined}
            mask=""
            maskChar=""
            autocomplete="off"
            readOnly={false}
            maxLength={undefined}
            min={undefined}
            description=""
            onKeyPress={() => undefined}
          />

          {isRescheduled && (
            <>
              {isLoadingScheduleData && (
                <Box display="flex" justify="center" mt="20">
                  <Loader />
                </Box>
              )}

              {hasScheduleDataError && (
                <p className="schedule-error">
                  Não foi possível carregar os dados de agendamento do pedido.
                </p>
              )}

              {!isLoadingScheduleData && scheduleData.cep && (
                <SelectAppointment
                  zipcode={scheduleData.cep}
                  clientId={orderDetailsQuery.data?.data?.client_id}
                  preOrderId={scheduleData.preOrderId}
                  onSelectAppointment={setRescheduleDate}
                />
              )}
            </>
          )}

          <Box display="flex" direction="column" gap="16">
            <Button
              width="full"
              minHeight="48"
              onClick={handleSave}
              disabled={isSaveDisabled}
            >
              {isSaving ? "Salvando..." : "Salvar"}
            </Button>
            <Button
              width="full"
              minHeight="48"
              outlined
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancelar
            </Button>
          </Box>
        </ModalChangeStatusStyle>
      )}
    />
  );
};
